import {
  Box,
  CircularProgress,
  keyframes,
  Menu,
  MenuItem,
  Stack,
  useTheme,
} from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MenuStartButton } from "shared-ts-mui";

const valueChangeAnimation = keyframes`
  0% {
    color: rgb(255, 255, 255, 0.2);
  }
  30% {
    color: rgb(255, 255, 255, 0.4);
  }
  70% {
    color: rgb(255, 255, 255, 0.8);
  }
  100% {
    color: rgb(255, 255, 255, 1);
  }
`;

declare module "@mui/x-data-grid-premium" {
  interface FooterPropsOverrides {
    rowCount: number;
    totalRowCount?: number;
    loading?: boolean;
    hideResetOptions?: boolean;
  }
}

export interface DataGridFooterWithToolbarProps {
  totalRowCount?: number;
  rowCount: number;
  loading?: boolean;
  hideResetOptions?: boolean;
}

export const DataGridFooterWithToolbar: FC<DataGridFooterWithToolbarProps> = (
  props
) => {
  const theme = useTheme();
  const apiRef = useGridApiContext();

  const { t } = useTranslation("shared-ts-ui-x-data-grid");
  const [animateTotalVisibleRows, setAnimateTotalVisibleRows] = useState(false);

  const handleResetFilter = () => {
    apiRef.current?.setFilterModel(
      {
        items: [],
      },
      "removeAllFilterItems"
    );
  };

  const handleResetSortOrder = () => {
    apiRef.current?.setSortModel([]);
  };

  useEffect(() => {
    setAnimateTotalVisibleRows(true);
    setTimeout(() => {
      setAnimateTotalVisibleRows(false);
    }, 1000);
  }, [props.rowCount, props.totalRowCount]);
  const handleExportToCSV = () => {
    apiRef.current.exportDataAsCsv();
  };

  const handleExportToExcel = () => {
    apiRef.current.exportDataAsExcel();
  };

  const sx = useMemo(() => {
    return {
      minHeight: 52,
      borderTop: "1px solid " + theme.palette.divider,
      paddingLeft: "1em",
      paddingRight: "1em",
      display: "flex",
      alignItems: "center",

      // Target direct child with class 'rightSide'
      "& > .rightSide": {
        paddingLeft: "1em", // Consider theme spacing
        marginLeft: "auto",

        // Target direct child with class 'totalVisibleRows' within '.rightSide'
        "& > .totalVisibleRows": {
          // Apply animation conditionally based on the prop
          animation: animateTotalVisibleRows
            ? `${valueChangeAnimation} 1s normal`
            : "none", // Or set to undefined to remove the property entirely
        },
      },
    };
  }, [animateTotalVisibleRows, theme.palette.divider]);

  return (
    <Box sx={sx}>
      <Stack
        className={"actions"}
        direction={"row"}
        sx={{ gap: 1, alignItems: "center" }}
      >
        <MenuStartButton
          label={t("Export")}
          ButtonProps={{
            color: "secondary",
          }}
          menu={(open, anchorEl, onClose) => (
            <Menu
              open={open}
              anchorEl={anchorEl}
              onClose={onClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
            >
              <MenuItem onClick={handleExportToCSV}>{t("CSV")}</MenuItem>
              <MenuItem onClick={handleExportToExcel}>{t("Excel")}</MenuItem>
            </Menu>
          )}
        />
        {props.hideResetOptions !== true && (
          <MenuStartButton
            label={t("Reset")}
            ButtonProps={{
              color: "secondary",
            }}
            menu={(open, anchorEl, onClose) => (
              <Menu
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
              >
                <MenuItem onClick={handleResetFilter}>{t("Filter")}</MenuItem>
                <MenuItem onClick={handleResetSortOrder}>
                  {t("Sort Order")}
                </MenuItem>
              </Menu>
            )}
          />
        )}
      </Stack>
      <Stack direction={"row"} className={"rightSide"}>
        {props.loading === true && (
          <CircularProgress size={"1em"} sx={{ mr: 1 }} />
        )}
        <Box component={"span"} sx={{ pr: 2 }}>
          {apiRef.current.getLocaleText("footerTotalRows")}
        </Box>
        <Box component={"span"} className={"totalVisibleRows"}>
          {apiRef.current.getLocaleText("footerTotalVisibleRows")(
            props.rowCount,
            props.totalRowCount ?? -1
          )}
        </Box>
      </Stack>
    </Box>
  );
};
